import { ReactNode } from 'react';

import { Dropdown } from './Dropdown';

interface PaginationProps {
  currentPage: number;
  hasMore: boolean;
  hasPrevious: boolean;
  pageSize: number;
  currentItemsCount: number;
  totalPages?: number;
  onPageChange: (page: number) => void;
  /** Optional control rendered to the left of the previous/next buttons. */
  actions?: ReactNode;
  /** Selectable page sizes. Renders a dropdown when provided with onPageSizeChange. */
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
}

export function Pagination({
  currentPage,
  hasMore,
  hasPrevious,
  pageSize,
  currentItemsCount,
  totalPages,
  onPageChange,
  actions,
  pageSizeOptions,
  onPageSizeChange,
}: PaginationProps) {
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = startIndex + currentItemsCount - 1;

  const approxTotal =
    totalPages !== undefined ? (hasMore ? totalPages * pageSize : endIndex) : undefined;

  return (
    <div className="flex flex-col gap-3 border-t border-gray-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
      <div className="flex items-center justify-center gap-3 text-center text-sm text-gray-500 sm:justify-start sm:text-left">
        <span>
          Showing {startIndex}-{endIndex}
          {approxTotal !== undefined && ` of ${hasMore ? '~' : ''}${approxTotal} results`}
          <span className="mx-3 hidden text-gray-300 sm:inline">|</span>
          <span className="block sm:inline">
            Page {currentPage}
            {totalPages !== undefined ? ` of ${totalPages}${hasMore ? '+' : ''}` : ''}
          </span>
        </span>
        {pageSizeOptions && onPageSizeChange && (
          <div className="w-32">
            <Dropdown
              value={String(pageSize)}
              onChange={(v) => onPageSizeChange(Number(v))}
              options={pageSizeOptions.map((n) => ({ value: String(n), label: `${n} / page` }))}
              ariaLabel="Results per page"
            />
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-2 sm:justify-end">
        {actions}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!hasPrevious}
          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4"
        >
          &lt; Previous
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!hasMore}
          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4"
        >
          Next &gt;
        </button>
      </div>
    </div>
  );
}
